import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Brain, BarChart3, TrendingUp, ChevronDown } from 'lucide-react';
import { getPaymentLinkUrl } from '../stripeConfig';
import { updatePageSEO } from '../utils/seo';

const PricingPage = ({
  onClose,
  selectedCountry = 'us',
  currentUser = null,
  onSignIn = null
}) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState(selectedCountry);
  const [showFaqs, setShowFaqs] = useState(false);
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' or 'faqs'
  const [upgrading, setUpgrading] = useState(false);

  const pricing = {
    us: {
      monthly: { price: '$5.99', period: '/month', yearly: false },
      yearly: { price: '$65', period: '/year', yearly: true, savings: 'Save $7' }
    },
    uk: {
      monthly: { price: '£4.99', period: '/month', yearly: false },
      yearly: { price: '£49.99', period: '/year', yearly: true, savings: 'Save £10' }
    }
  };

  const currentPricing = pricing[currency] || pricing.us;
  const displayPrice = currentPricing[billingCycle];

  // Update SEO tags when component mounts
  useEffect(() => {
    updatePageSEO({
      title: 'Pricing - PlanLens.ai | Affordable AI-Powered Financial Planning',
      description: 'Start free with PlanLens.ai. Upgrade to Premium for $5.99/month and unlock AI-powered portfolio analysis, advanced retirement planning, Monte Carlo simulations, and unlimited accounts.',
      canonical: 'https://planlens.ai/pricing'
    });
  }, []);

  // Handle upgrade button click
  const handleStartTrial = async () => {
    // If user is not logged in, trigger sign-in flow
    if (!currentUser) {
      if (onSignIn) {
        onSignIn(); // This should trigger the authentication flow
      } else {
        alert('Please sign in or create an account to start your trial.');
      }
      return;
    }

    // User is logged in, redirect to Stripe payment page
    setUpgrading(true);
    try {
      const country = currency.toUpperCase(); // 'US' or 'UK'
      const email = currentUser.email;
      const uid = currentUser.uid;

      // Get Payment Link URL with selected billing cycle
      const paymentUrl = getPaymentLinkUrl(country, billingCycle, email, uid);

      // Redirect to Stripe Payment Link
      window.location.href = paymentUrl;
    } catch (error) {
      console.error('Error starting trial:', error);
      alert('Failed to start trial. Please try again or contact support.');
      setUpgrading(false);
    }
  };

  const freeFeatures = [
    "Portfolio tracking & account management",
    "Multi-country support (US & UK)",
    "Financial goals tracking & projections",
    "Dark mode & mobile responsive",
    "Cloud sync across devices",
    "CSV import/export"
  ];

  const premiumFeatures = [
    "Everything in Free, plus:",
    "Net worth dashboard with health score",
    "Liabilities & debt management",
    "AI Financial Health Check (portfolio, debt, goals, priorities)",
    "Enhanced retirement planning with scenario analysis",
    "Historical backtesting (1995-2024)",
    "Monte Carlo simulations (10,000 scenarios)",
    "Risk assessment & diversification scores",
    "Advanced scenario planning",
    "Retirement drawdown simulator",
    "Portfolio optimization recommendations",
    "Country-specific tax optimization",
    "Priority email support",
    "Export reports to PDF",
    "Advanced analytics & insights",
    "Early access to new features"
  ];

  const faqs = [
    {
      q: "Can I switch between monthly and yearly billing?",
      a: "Yes! You can upgrade to yearly billing anytime to save money. The system will prorate your current monthly subscription."
    },
    {
      q: "What payment methods do you accept?",
      a: "We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through our secure payment processor."
    },
    {
      q: "Can I cancel anytime?",
      a: "Absolutely! Cancel anytime with just one click. No questions asked, no cancellation fees. Your data remains accessible even after cancellation."
    },
    {
      q: "What happens after the 14-day free trial?",
      a: "After your free trial ends, you'll be automatically converted to the paid plan unless you cancel. We'll send you reminder emails before charging."
    },
    {
      q: "Is my financial data secure?",
      a: "Yes! We use bank-level encryption and industry-standard security practices. Your data is stored securely in Firebase with end-to-end encryption."
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] flex flex-col">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 sm:p-4 rounded-t-2xl flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-bold">Simple Pricing</h2>
            </div>

            {/* Currency and Billing Toggles - Horizontal on all screens */}
            <div className="flex items-center gap-2 sm:gap-3 mx-2 sm:mx-4">
              <div className="flex items-center gap-1 bg-blue-500 bg-opacity-50 rounded-lg px-1.5 py-1">
                <button
                  onClick={() => setCurrency('us')}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    currency === 'us' ? 'bg-white text-blue-600' : 'text-white'
                  }`}
                >
                  🇺🇸
                </button>
                <button
                  onClick={() => setCurrency('uk')}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    currency === 'uk' ? 'bg-white text-blue-600' : 'text-white'
                  }`}
                >
                  🇬🇧
                </button>
              </div>

              <div className="flex items-center gap-1.5">
                <span className={`text-xs font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-blue-200'}`}>Mo</span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    billingCycle === 'yearly' ? 'bg-green-500' : 'bg-blue-400'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    billingCycle === 'yearly' ? 'transform translate-x-5' : ''
                  }`} />
                </button>
                <span className={`text-xs font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-blue-200'}`}>Yr</span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-3 border-t border-blue-400 pt-3">
            <button
              onClick={() => setActiveTab('plans')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'plans' ? 'bg-white text-blue-600' : 'bg-blue-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              Plans & Features
            </button>
            <button
              onClick={() => setActiveTab('faqs')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'faqs' ? 'bg-white text-blue-600' : 'bg-blue-500 bg-opacity-50 text-white hover:bg-opacity-70'
              }`}
            >
              FAQs
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
          {activeTab === 'plans' ? (
            <>
              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                {/* Free Plan */}
                <div className="border-2 border-gray-200 rounded-xl p-4 sm:p-5 bg-white hover:shadow-lg transition-shadow">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Free</h3>
                    <div className="flex items-baseline justify-center gap-1 mb-1">
                      <span className="text-4xl font-bold text-gray-900">
                        {currency === 'us' ? '$' : '£'}0
                      </span>
                      <span className="text-sm text-gray-600">/forever</span>
                    </div>
                    <p className="text-xs text-gray-600">Perfect for getting started</p>
                  </div>

                  <ul className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                    {freeFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={onClose}
                    className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors text-sm"
                  >
                    Start Free
                  </button>
                </div>

                {/* Premium Plan */}
                <div className="border-2 border-purple-400 rounded-xl p-4 sm:p-5 bg-gradient-to-br from-blue-50 to-purple-50 relative hover:shadow-xl transition-shadow">
                  <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      POPULAR
                    </span>
                  </div>

                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Premium</h3>
                    <div className="flex items-baseline justify-center gap-1 mb-1">
                      <span className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {displayPrice.price}
                      </span>
                      <span className="text-sm text-gray-600">{displayPrice.period}</span>
                    </div>
                    {billingCycle === 'yearly' && (
                      <span className="inline-block text-xs bg-green-500 text-white px-2 py-0.5 rounded-full mb-1">
                        {displayPrice.savings}
                      </span>
                    )}
                    <p className="text-xs text-gray-600">14-day free trial</p>
                  </div>

                  <ul className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                    {premiumFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          index === 0 ? 'text-gray-900' : 'text-purple-600'
                        }`} />
                        <span className={`text-xs ${index === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={handleStartTrial}
                    disabled={upgrading}
                    className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-colors shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {upgrading ? 'Redirecting...' : 'Start 14-Day Free Trial'}
                  </button>
                  <p className="text-center text-xs text-gray-600 mt-2">
                    {currentUser ? '14-day trial, then billed' : 'Sign in to start trial'}
                  </p>
                </div>
              </div>

              {/* Key Benefits - Compact Horizontal Layout */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 text-center mb-4">Why Upgrade?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="flex items-start gap-3 sm:flex-col sm:items-center sm:text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">Financial Health Check</h4>
                      <p className="text-xs text-gray-600">Comprehensive AI analysis</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:flex-col sm:items-center sm:text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">Analytics</h4>
                      <p className="text-xs text-gray-600">Backtesting & simulations</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:flex-col sm:items-center sm:text-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm mb-0.5">Retirement</h4>
                      <p className="text-xs text-gray-600">Enhanced scenario planning</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* FAQs Tab */
            <div className="space-y-3">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
              {faqs.map((faq, index) => (
                <details key={index} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow group">
                  <summary className="font-semibold text-gray-900 cursor-pointer text-sm sm:text-base flex items-center justify-between">
                    {faq.q}
                    <ChevronDown className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2" />
                  </summary>
                  <p className="text-gray-600 mt-2 text-xs sm:text-sm">{faq.a}</p>
                </details>
              ))}

              {/* Additional FAQs */}
              <details className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow group">
                <summary className="font-semibold text-gray-900 cursor-pointer text-sm sm:text-base flex items-center justify-between">
                  Can I use PlanLens in multiple countries?
                  <ChevronDown className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2" />
                </summary>
                <p className="text-gray-600 mt-2 text-xs sm:text-sm">
                  Yes! You can switch between US and UK settings anytime. The app automatically adjusts currency, account types, and tax rules based on your selection.
                </p>
              </details>

              <details className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow group">
                <summary className="font-semibold text-gray-900 cursor-pointer text-sm sm:text-base flex items-center justify-between">
                  Do you offer discounts for students or nonprofits?
                  <ChevronDown className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2" />
                </summary>
                <p className="text-gray-600 mt-2 text-xs sm:text-sm">
                  Yes! Contact our support team with proof of student status or nonprofit documentation for special pricing.
                </p>
              </details>
            </div>
          )}
        </div>

        {/* Compact Footer CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 sm:p-4 rounded-b-2xl flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-center sm:text-left">
              <h3 className="text-sm sm:text-base font-bold">Ready to take control?</h3>
              <p className="text-xs text-blue-100">Join thousands planning smarter</p>
            </div>
            <button
              onClick={() => {
                if (!currentUser && onSignIn) {
                  onSignIn(); // Direct to sign-in for free plan
                } else {
                  onClose(); // Close if already logged in
                }
              }}
              className="px-6 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-sm whitespace-nowrap"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
